import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Users, TrendingUp, Zap } from 'lucide-react';
import mockData from '../data/mock';

const HeroSection = () => {
  const { hero } = mockData;

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-[#2D9CDB]/10 rounded-full blur-xl"></div>
      <div className="absolute bottom-32 right-16 w-32 h-32 bg-slate-900/5 rounded-full blur-xl"></div>
      <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-[#2D9CDB]/5 rounded-full blur-lg"></div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Main Tagline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              <span className="text-[#2D9CDB]">Saling Untung</span> –{' '}
              <br />
              <span className="text-slate-900">Driver Untung</span>, 
              <span className="text-[#2D9CDB]"> Merchant Untung</span>,{' '}
              <br />
              <span className="bg-gradient-to-r from-[#2D9CDB] to-slate-900 bg-clip-text text-transparent">
                Semua Untung
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-slate-600 mb-12 leading-relaxed">
              {hero.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
              <Link to="/merchants" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 group"
                >
                  Lihat Merchant yang Terdaftar
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-6 text-center lg:text-left">
              <div>
                <div className="text-2xl font-bold text-[#2D9CDB] mb-1">700+</div>
                <div className="text-sm text-slate-600">Driver Aktif</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-[#2D9CDB] mb-1">500+</div>
                <div className="text-sm text-slate-600">Agent Tour & Travel</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-[#2D9CDB] mb-1">85%</div>
                <div className="text-sm text-slate-600">Tingkat Kepuasan</div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <img 
                src={hero.heroImage}
                alt="Driver wisata profesional dengan kendaraan pariwisata - Platform SalingUntung menghubungkan driver tour dengan merchant di Bandung, Yogyakarta, Bali, Malang"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t to-transparent"></div>
              {/* <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4">
                  <h3 className="font-bold text-slate-900 mb-2">Platform Terintegrasi</h3>
                  <p className="text-sm text-slate-600">Menghubungkan driver pariwisata dengan berbagai jenis merchant</p>
                </div>
              </div> */}
            </div>

            {/* Floating cards */}
            <div className="absolute -top-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-slate-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#2D9CDB] rounded-full flex items-center justify-center mr-3">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Driver</div>
                  <div className="text-xs text-slate-600">Membawa Tamu</div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-4 border border-slate-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center mr-3">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Merchant</div>
                  <div className="text-xs text-slate-600">Omzet Meningkat</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Visual Flow Illustration - Bottom Section */}
        <div className="mt-20">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-slate-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Alur Kerja SalingUntung</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex flex-col items-center text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-[#2D9CDB] to-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Driver → Merchant</h4>
                <p className="text-slate-600 text-sm">Driver membawa wisatawan ke merchant partner</p>
              </div>

              <div className="flex flex-col items-center text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-[#2D9CDB] to-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Transaksi</h4>
                <p className="text-slate-600 text-sm">Merchant input nominal belanja wisatawan</p>
              </div>

              <div className="flex flex-col items-center text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-[#2D9CDB] to-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Komisi Otomatis</h4>
                <p className="text-slate-600 text-sm">Sistem hitung komisi dan fee otomatis</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#2D9CDB] rounded-full flex justify-center">
          <div className="w-1 h-3 bg-[#2D9CDB] rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;